import * as _ from "lodash";
import * as React from "react";
import { useMappedState } from "redux-react-hook";

import { InfoBar } from "src/library/components/InfoBar";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { Address } from "src/library/components/Address";
import {
  EditPurchaseOrderNumber,
  AddPurchaseOrderNumber
} from "src/core/shared/components/PurchaseOrderNumber";
import { ListMode } from "src/core/lists/store/lists.reducer";
import { IOrderStatus } from "src/core/orders/api/orders.api";
import { usePriceVisibility } from "src/core/prices/hooks/use-price-visibility";
import { TotalPrice } from "src/core/prices/components/TotalPrice";
import EditListName from "src/core/shared/components/EditListName";
import { IStore } from "src/core/shared/store";
import { Link } from "src/library/components/Link";
import { totalWeight } from "src/core/orders/utils/orders";
import { Text } from "src/library/components/Text";
import OrderVariantLabel from "src/core/orders/components/orderVariantLabel";

import { IList } from "../../api/lists.api";

interface IProps {
  isLoading: boolean;
  viewMode: ListMode;
  data: IList | null;
  primaryAction?: JSX.Element | null;
  secondaryAction?: JSX.Element| null;
  terminology: ITerminology;
  orderStatuses: IOrderStatus[];
  scheduleViewSetting: "orders" | "schedule" | null;
  setPONumber: (PONumber: string) => void;
  editListName: (listName: string) => void;
}

const ListInfoSidebar = React.memo(
  ({
    isLoading,
    viewMode,
    data,
    primaryAction,
    secondaryAction,
    terminology,
    setPONumber,
    editListName
  }: IProps) => {
    const pricesVisible = usePriceVisibility();

    const { roleType, orderVariants } = useMappedState(({ global }: IStore) => ({
      roleType: global.role ? global.role.type : null,
      orderVariants: global.settings?.orderVariants || []
    }));

    const orderVariant = React.useMemo(() => {
      return data?.orderVariant ?? _.find(orderVariants, ov => ov.id === data?.orderVariantId);
    }, [ data, orderVariants ]);

    const totalListWeight = React.useMemo(() => {
      return totalWeight(data);
    }, [ data ]);

    const items = [
      {
        label: "Name",
        value:
        data && data.name ? (
          <EditListName
            isEditable={viewMode === "edit"}
            listName={data.name}
            editListName={editListName}
          />
        ) : "-"
      },
      orderVariant && orderVariants.length > 1 && {
        label: `${terminology.orderVariant?.singular.text ?? "Order Variant"}`,
        value: <OrderVariantLabel
          orderVariant={orderVariant}
          place="left"
               />
      },
      {
        label: "Total quantity",
        value: data ? _.sumBy(data.listLines, listLine => listLine.quantity) : "-"
      },

      totalListWeight !== null ? {
        label: "Total weight",
        value: (
          <Text>
            {`${totalListWeight}kg`}
          </Text>
        )
      } : null,
      {
        label: "Purchase Order No.",
        value:
          data && data.customerReference ? (
            <EditPurchaseOrderNumber
              isEditable={viewMode === "edit"}
              type="list"
              customerReference={data.customerReference}
              setPONumber={setPONumber}
            />
          ) : viewMode === "edit" ? (
            <AddPurchaseOrderNumber type="list" />
          ) : "-"
      },
      {
        label: `${terminology.customer.singular.text}`,
        value: data?.branch?.customer ? roleType === "admin" ? (
          <Link
            color="primary"
            textTransform="capitalize"
            type="underlined"
            to={`/admin/customers/${data?.branch?.customerId}/drops`}
          >
            {`${data.branch.customer.name} (${data.branch.customer.accountNumber})`}
          </Link>
        ) : `${data.branch.customer.name} (${data.branch.customer.accountNumber})` : "-"
      },
      {
        label: `${terminology.drop.singular.text}`,
        value: data?.branch ? roleType === "admin" ? (
          <Link
            color="primary"
            textTransform="capitalize"
            type="underlined"
            to={`/admin/customers/${data?.branch?.customerId}/drops/${data?.branchId}/lists`}
          >
            {`${data.branch?.name} (${data.branch.branchNumber})`}
          </Link>
        ) : `${data.branch?.name} (${data.branch.branchNumber})` : "-"
      },
      {
        label: "Delivery address",
        value: data?.branch ? <Address address={data.branch.address} /> : "-"
      }
    ];

    if (pricesVisible) {
      items.splice(1, 0, {
        label: "Total",
        value: (data ? (
          <TotalPrice
            lines={data.listLines}
            id={data.id}
            currency={data.branch.priceCode?.currency}
          />
        ) : "-")
      });
    }

    return (
      <InfoBar
        isLoading={isLoading}
        title={`${_.capitalize(terminology.list.singular.text)} Information`}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        items={_.compact(items)}
      />
    );
  }
);

export default ListInfoSidebar;
